import React, { useMemo, useState } from 'react';
import { Search, Edit2, Trash2 } from 'lucide-react';
import useAssessmentManagementStore from '@/stores/AdminStore/AssessmentManagementStore';
import type { Question, QuestionPayload } from '@/services/PagesServices/AssessmentManagementService';
import './QuestionBank.css';

type QType = '' | 'MCQ' | 'T/F' | 'Essay' | 'Short';
type Difficulty = '' | 'easy' | 'medium' | 'hard';

const EMPTY_OPTIONS = ['', '', '', ''];
const TF_OPTIONS = ['True', 'False'];

const optionsForType = (t: QType): string[] => {
  if (t === 'T/F') return [...TF_OPTIONS];
  if (t === 'MCQ') return [...EMPTY_OPTIONS];
  return [...EMPTY_OPTIONS];
};

const QuestionBank: React.FC = () => {
  const { questions, addQuestion, updateQuestion, deleteQuestion } =
    useAssessmentManagementStore();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('All Topics');
  const [selectedType, setSelectedType] = useState('All Types');

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [questionType, setQuestionType] = useState<QType>('');
  const [topic, setTopic] = useState('');
  const [difficulty, setDifficulty] = useState<Difficulty>('');
  const [questionText, setQuestionText] = useState('');
  const [mandatory, setMandatory] = useState(false);
  const [points, setPoints] = useState<number>(10);
  const [options, setOptions] = useState<string[]>(EMPTY_OPTIONS);
  const [correctAnswerIndex, setCorrectAnswerIndex] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const filteredQuestions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return (questions ?? []).filter((q) => {
      if (selectedTopic !== 'All Topics' && q.topic !== selectedTopic) return false;
      if (selectedType !== 'All Types' && q.type !== selectedType) return false;
      if (!term) return true;
      return (
        (q.question ?? '').toLowerCase().includes(term) ||
        (q.topic ?? '').toLowerCase().includes(term)
      );
    });
  }, [questions, searchTerm, selectedTopic, selectedType]);

  const getTypeClass = (type: string) => {
    const typeMap: Record<string, string> = {
      'MCQ': 'trainer-assessment-type-mcq',
      'T/F': 'trainer-assessment-type-tf',
      'Essay': 'trainer-assessment-type-essay',
      'Short': 'trainer-assessment-type-short'
    };
    return typeMap[type] || '';
  };

  const getDifficultyClass = (d: string) => `trainer-assessment-difficulty-${d}`;

  const resetForm = () => {
    setEditingId(null);
    setQuestionType('');
    setTopic('');
    setDifficulty('');
    setQuestionText('');
    setMandatory(false);
    setPoints(10);
    setOptions(EMPTY_OPTIONS);
    setCorrectAnswerIndex(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  const openEditModal = (q: Question) => {
    setEditingId(q.id);
    setQuestionType(q.type);
    setTopic(q.topic ?? '');
    setDifficulty(q.difficulty);
    setQuestionText(q.question ?? '');
    setPoints(q.points ?? 10);
    setMandatory(false);

    let opts: string[];
    if (q.type === 'T/F') {
      opts = q.options && q.options.length >= 2 ? q.options.slice(0, 2) : [...TF_OPTIONS];
    } else if (q.type === 'MCQ') {
      opts = q.options && q.options.length > 0
        ? [...q.options, ...EMPTY_OPTIONS].slice(0, Math.max(4, q.options.length))
        : [...EMPTY_OPTIONS];
    } else {
      opts = [...EMPTY_OPTIONS];
    }
    setOptions(opts);
    setCorrectAnswerIndex(
      q.correctAnswer ? opts.indexOf(q.correctAnswer) : null
    );
    setShowAddModal(true);
  };

  const handleTypeChange = (next: QType) => {
    if (next === questionType) return;
    setQuestionType(next);
    setOptions(optionsForType(next));
    setCorrectAnswerIndex(null);
  };

  const closeModal = () => {
    setShowAddModal(false);
    resetForm();
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this question? This cannot be undone.')) return;
    try {
      await deleteQuestion(id);
    } catch (err: any) {
      window.alert(err?.message || 'Failed to delete question.');
    }
  };

  const handleSave = async () => {
    if (submitting) return;
    if (!questionType) {
      window.alert('Please select a question type.');
      return;
    }
    if (!questionText.trim()) {
      window.alert('Please enter the question text.');
      return;
    }
    const isChoice = questionType === 'MCQ' || questionType === 'T/F';
    const trimmedOptions = options.map((o) => o.trim()).filter(Boolean);
    if (isChoice && trimmedOptions.length < 2) {
      window.alert('Please provide at least two options.');
      return;
    }
    if (isChoice && correctAnswerIndex === null) {
      window.alert('Please mark the correct answer.');
      return;
    }

    const correctAnswer = isChoice && correctAnswerIndex !== null
      ? options[correctAnswerIndex]?.trim()
      : undefined;

    const payload: QuestionPayload = {
      type: questionType as Exclude<QType, ''>,
      difficulty: (difficulty || 'medium') as Exclude<Difficulty, ''>,
      topic: topic || 'General',
      points: Number.isFinite(points) ? points : 10,
      question: questionText.trim(),
      options: isChoice ? trimmedOptions : undefined,
      correctAnswer,
    };

    setSubmitting(true);
    try {
      if (editingId !== null) {
        await updateQuestion(editingId, payload);
      } else {
        await addQuestion(payload);
      }
      closeModal();
    } catch (err: any) {
      window.alert(err?.message || 'Failed to save question.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="trainer-assessment-question-bank">
      <div className="trainer-assessment-bank-header">
        <h3 className="trainer-assessment-bank-title">Question Repository</h3>
        <button className="trainer-assessment-add-question-btn" onClick={openCreateModal}>
          + Add Question
        </button>
      </div>

      <div className="trainer-assessment-bank-filters">
        <div className="trainer-assessment-search-wrapper">
          <Search size={18} className="trainer-assessment-search-icon" />
          <input
            type="text"
            placeholder="Search Question"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="trainer-assessment-search-input"
          />
        </div>
        <select
          className="trainer-assessment-filter-dropdown"
          value={selectedTopic}
          onChange={(e) => setSelectedTopic(e.target.value)}
        >
          <option>All Topics</option>
          <option>Leadership</option>
          <option>Programming</option>
          <option>Technical</option>
          <option>Project Management</option>
        </select>
        <select
          className="trainer-assessment-filter-dropdown"
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
        >
          <option>All Types</option>
          <option>MCQ</option>
          <option>T/F</option>
          <option>Essay</option>
          <option>Short</option>
        </select>
      </div>

      <div className="trainer-assessment-questions-list">
        {filteredQuestions.length === 0 ? (
          <div className="trainer-assessment-empty-state">No questions match your search.</div>
        ) : null}
        {filteredQuestions.map((q) => (
          <div key={q.id} className="trainer-assessment-question-item">
            <div className="trainer-assessment-question-item-header">
              <div className="trainer-assessment-question-badges">
                <span className={`trainer-assessment-type-badge ${getTypeClass(q.type)}`}>{q.type}</span>
                <span className={`trainer-assessment-difficulty-badge ${getDifficultyClass(q.difficulty)}`}>
                  {q.difficulty}
                </span>
                <span className="trainer-assessment-topic-badge">{q.topic}</span>
                <span className="trainer-assessment-points-badge">{q.points} pts</span>
              </div>
              <div className="trainer-assessment-question-actions">
                <button
                  type="button"
                  className="trainer-assessment-icon-btn trainer-assessment-icon-btn-edit"
                  onClick={() => openEditModal(q)}
                  aria-label="Edit question"
                >
                  <Edit2 size={16} />
                </button>
                <button
                  type="button"
                  className="trainer-assessment-icon-btn trainer-assessment-icon-btn-delete"
                  onClick={() => handleDelete(q.id)}
                  aria-label="Delete question"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
            <p className="trainer-assessment-question-text">{q.question}</p>
            {q.options && (
              <div className="trainer-assessment-question-options-grid">
                {q.options.map((option, idx) => (
                  <div
                    key={idx}
                    className={`trainer-assessment-option-item ${option === q.correctAnswer ? 'trainer-assessment-correct-answer' : ''}`}
                  >
                    {option}
                    {option === q.correctAnswer && (
                      <span className="trainer-assessment-correct-indicator">✓</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {showAddModal && (
        <div className="trainer-assessment-modal-overlay" onClick={closeModal}>
          <div className="trainer-assessment-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="trainer-assessment-modal-header">
              <div className="trainer-assessment-modal-title-wrapper">
                <span className="trainer-assessment-modal-icon">?</span>
                <h3>{editingId !== null ? 'Edit Question' : 'Create New Question'}</h3>
              </div>
              <button className="trainer-assessment-modal-close" onClick={closeModal}>×</button>
            </div>
            <div className="trainer-assessment-modal-body">
              <div className="trainer-assessment-form-row">
                <div className="trainer-assessment-form-field">
                  <label>Question Type</label>
                  <select
                    className="trainer-assessment-form-select"
                    value={questionType}
                    onChange={(e) => handleTypeChange(e.target.value as QType)}
                  >
                    <option value="">Select</option>
                    <option value="MCQ">MCQ</option>
                    <option value="T/F">T/F</option>
                    <option value="Essay">Essay</option>
                    <option value="Short">Short</option>
                  </select>
                </div>
                <div className="trainer-assessment-form-field">
                  <label>Topic</label>
                  <select
                    className="trainer-assessment-form-select"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                  >
                    <option value="">Select</option>
                    <option value="Leadership">Leadership</option>
                    <option value="Programming">Programming</option>
                    <option value="Technical">Technical</option>
                    <option value="Project Management">Project Management</option>
                  </select>
                </div>
                <div className="trainer-assessment-form-field">
                  <label>Difficulty</label>
                  <select
                    className="trainer-assessment-form-select"
                    value={difficulty}
                    onChange={(e) => setDifficulty(e.target.value as Difficulty)}
                  >
                    <option value="">Select</option>
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>
              </div>

              <div className="trainer-assessment-toggle-field">
                <span>Make Question mandatory?</span>
                <label className="trainer-assessment-toggle">
                  <input
                    type="checkbox"
                    checked={mandatory}
                    onChange={(e) => setMandatory(e.target.checked)}
                  />
                  <span className="trainer-assessment-toggle-slider"></span>
                </label>
              </div>

              <div className="trainer-assessment-form-field">
                <label>Question</label>
                <textarea
                  className="trainer-assessment-form-textarea"
                  placeholder="Enter your question..."
                  rows={3}
                  value={questionText}
                  onChange={(e) => setQuestionText(e.target.value)}
                />
              </div>

              {(questionType === 'MCQ' || questionType === 'T/F') && (
                <div className="trainer-assessment-form-field">
                  <label>Options (Click radio button to mark correct answer)</label>
                  <div className="trainer-assessment-options-list">
                    {options.map((option, idx) => (
                      <div key={idx} className="trainer-assessment-option-input-row">
                        <input
                          type="radio"
                          name="correctAnswer"
                          checked={correctAnswerIndex === idx}
                          onChange={() => setCorrectAnswerIndex(idx)}
                          className="trainer-assessment-radio-input"
                        />
                        <input
                          type="text"
                          placeholder={`Option ${idx + 1}`}
                          value={option}
                          readOnly={questionType === 'T/F'}
                          onChange={(e) => {
                            if (questionType === 'T/F') return;
                            const newOptions = [...options];
                            newOptions[idx] = e.target.value;
                            setOptions(newOptions);
                          }}
                          className="trainer-assessment-option-input"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="trainer-assessment-form-field">
                <label>Points</label>
                <input
                  type="number"
                  className="trainer-assessment-form-input"
                  value={points}
                  onChange={(e) => setPoints(Number(e.target.value))}
                />
              </div>
            </div>
            <div className="trainer-assessment-modal-footer">
              <button
                className="trainer-assessment-btn-save"
                onClick={handleSave}
                disabled={submitting}
              >
                <span>💾</span>{' '}
                {submitting
                  ? 'Saving…'
                  : editingId !== null
                  ? 'Update Question'
                  : 'Save Question'}
              </button>
              <button className="trainer-assessment-btn-cancel" onClick={closeModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionBank;
